import { Howl } from 'howler';
import { useCallback, useEffect, useRef } from 'react';

const DISPETCHER_SOUNDS = {
  batteryInserted: { path: '/sounds/dialogs/Ch1_Disp_S1_01.mp3' },
  wrongSolutions: {
    wrongSolution1: { path: '/sounds/dialogs/Ch1_Disp_R1_01.mp3' },
    wrongSolution2: { path: '/sounds/dialogs/Ch1_Disp_R1_02.mp3' },
    wrongSolution3: { path: '/sounds/dialogs/Ch1_Disp_R1_03.mp3' },
    wrongSolution4: { path: '/sounds/dialogs/Ch1_Disp_R1_04.mp3' },
    wrongSolution5: { path: '/sounds/dialogs/Ch1_Disp_R1_05.mp3' },
  },
  keyUseds: {
    keyUsed1: { path: '/sounds/dialogs/Ch1_Disp_RM1_01.mp3' },
    keyUsed2: { path: '/sounds/dialogs/Ch1_Disp_RM1_02.mp3' },
  },
  namelessWrongAnswer: {
    echo1: { path: '/sounds/dialogs/Ch1_Echo_R1_01.mp3' },
    echo2: { path: '/sounds/dialogs/Ch1_Echo_R1_02.mp3' },
    echo3: { path: '/sounds/dialogs/Ch1_Echo_R1_03.mp3' },
    wrongSolution1: { path: '/sounds/dialogs/Ch1_Disp_R1_01.mp3' },
    wrongSolution2: { path: '/sounds/dialogs/Ch1_Disp_R1_02.mp3' },
    wrongSolution3: { path: '/sounds/dialogs/Ch1_Disp_R1_03.mp3' },
    wrongSolution4: { path: '/sounds/dialogs/Ch1_Disp_R1_04.mp3' },
    wrongSolution5: { path: '/sounds/dialogs/Ch1_Disp_R1_05.mp3' },
  },
  numbersStartDialog: {
    Ch1_Disp_Numbers_01: { path: '/sounds/dialogs/Ch1_Disp_Numbers_01.mp3' },
  },
  numbersDialogs: {
    Ch1_Disp_Numbers_02: { path: '/sounds/dialogs/Ch1_Disp_Numbers_02.mp3' },
    Ch1_Disp_Numbers_03: { path: '/sounds/dialogs/Ch1_Disp_Numbers_03.mp3' },
    Ch1_Disp_Numbers_04: { path: '/sounds/dialogs/Ch1_Disp_Numbers_04.mp3' },
    Ch1_Disp_Numbers_05: { path: '/sounds/dialogs/Ch1_Disp_Numbers_05.mp3' },
    Ch1_Disp_Numbers_06: { path: '/sounds/dialogs/Ch1_Disp_Numbers_06.mp3' },
  },
  oscilloscopeStartDialog: {
    Ch1_Narrator_Os_R1: { path: '/sounds/oscilloscope_01/Ch1_Narrator_Os_R1.mp3'}
  }, 
  oscilloscope_01: {
    start: { path: '/sounds/oscilloscope_01/start.mp3' }
  },
  oscilloscope_02: {
    first_interaction: { path: '/sounds/oscilloscope_02/first_interaction.mp3' },
    second_answer: { path: '/sounds/oscilloscope_02/second_answer.mp3' }
  },
  oscilloscope_03: {
    second_interaction: { path: '/sounds/oscilloscope_03/second_interaction.mp3' },
    dispatcher_second_interaction: { path: '/sounds/oscilloscope_03/dispatcher_second_interaction.mp3' }
  },
  cassete: {
    before_animation: { path: '/sounds/cassete/before_animation.mp3'},
    after_animation: { path: '/sounds/cassete/after_animation.mp3'},
  },
  playback: {
    CasseteAudio: { path: '/assets/playback/CasseteAudio.mp3'},
    dispatcher: { path: '/assets/playback/dispatcher.mp3'},
  },
  ringAuthor: {
    harlan: { path: '/sounds/ring/author_harlan.mp3' },
    ksarat: { path: '/sounds/ring/author_ksarat.mp3' },
    asterion: { path: '/sounds/ring/author_asterion.mp3' },
    vira: { path: '/sounds/ring/author_vira.mp3' },
    ruviy: { path: '/sounds/ring/author_ruviy.mp3' },
    morian: { path: '/sounds/ring/author_morian.mp3' },
    ailin: { path: '/sounds/ring/author_ailin.mp3' },
    safaris: { path: '/sounds/ring/author_safaris.mp3' },
  },
  call: {
    dispatcher: { path: '/sounds/call/dispatcher.mp3'}
  },
  error: {
    dispatcher: { path: '/sounds/error/dispatcher.mp3'}
  },
} as const


type DispatcherSoundsConfig = typeof DISPETCHER_SOUNDS
export type SoundKey<K extends keyof DispatcherSoundsConfig> = K extends K ? string & keyof DispatcherSoundsConfig[K] : never

export const useDispatcherSounds = <K extends keyof DispatcherSoundsConfig>({
  onSoundPlay,
  onSoundStop,
  soundsConfigKey
}: {
  onSoundPlay: (key: SoundKey<K>) => void
  onSoundStop: (key: SoundKey<K>) => void
  soundsConfigKey?: K

}) => {
  const soundsRef = useRef<Howl[]>([])
  const currentSoundRef = useRef<Howl | null>(null)

  useEffect(() => {
    if (!soundsConfigKey) return
    const config = DISPETCHER_SOUNDS[soundsConfigKey];
    if (!config) return;

    soundsRef.current = Object.entries(config).map(
      ([key, sound]) =>
        new Howl({
          src: [sound.path],
          volume: 1,
          preload: true,
          onplay: () => onSoundPlay(key as SoundKey<K>),
          onend: () => onSoundStop(key as SoundKey<K>),
        })
    )
    return () => {
      soundsRef.current.forEach((sound) => sound.unload())
      currentSoundRef.current?.stop()
    }
  }, [])

  const playSoundRandom = useCallback(() => {
    if (currentSoundRef.current) {
      currentSoundRef.current.stop()
    }
    if (soundsRef.current.length > 0) {
      const randomSound =
        soundsRef.current[
        Math.floor(Math.random() * soundsRef.current.length)
        ]
      currentSoundRef.current = randomSound
      randomSound.play()
    }
  }, [])

  const playSoundByKey = useCallback(async (key: SoundKey<K>) => {
    await new Promise((resolve)=> {
      if (currentSoundRef.current) {
        currentSoundRef.current.stop()
      }
      // @ts-ignore -- item._src is not public method(
      const sound = soundsRef.current.find(item => item._src.includes(key))
      if (sound) {
        currentSoundRef.current = sound
        sound.play()
        sound.once('end', ()=> {
          resolve(true)
        })
      } else {
        resolve(false)
      }
    })
  }, [])

  return {
    playSoundRandom,
    playSoundByKey
  }
}
